import { Form, Formik, type FormikHelpers } from 'formik';
import { normalizePhoneNumber } from 'gcs-common/helper/formHelper';
import { getPhoneNumberValidator, stringValidator } from 'gcs-common/helper/yupValidationHelper';
import {
  getCreateAgentDefaultErrorMessage,
  getCreateAgentErrorMessage,
} from 'gcs-common/slices/administration/administrationUsers/administrationUsersSelectors';
import {
  resetCreateAgentDefaultErrorMessage,
  resetCreateAgentErrorMessage,
} from 'gcs-common/slices/administration/administrationUsers/administrationUsersSlice';
import { createAgent } from 'gcs-common/slices/administration/administrationUsers/administrationUsersThunks';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { showFailureNotification } from '../../../../slices/notifications/ShowNotificationThunk';
import Button from '../../../Button/Button';
import FormikInput from '../../../FormikInput/FormikInput';
import FormikSelect from '../../../FormikSelect/FormikSelect';
import styles from './styles.module.scss';

const AgentCreateValidationSchema = Yup.object().shape({
  firstName: stringValidator,
  lastName: stringValidator,
  email: Yup.string().trim().email('Ungültige E-Mail Adresse').required('Erforderlich'),
  phoneNumber: getPhoneNumberValidator(),
});

const SALUTATION = [
  {
    key: 'Herr',
    value: 'Herr',
  },
  {
    key: 'Frau',
    value: 'Frau',
  },
];

interface AgentCreateFormValues {
  email: string;
  phoneNumber: string;
  firstName: string;
  lastName: string;
  salutation: string;
}

const initialData: AgentCreateFormValues = {
  email: '',
  phoneNumber: '',
  firstName: '',
  lastName: '',
  salutation: '',
};

interface AgentCreateProps {
  onCancel: VoidFunction;
  onSuccess: VoidFunction;
}

const AgentCreate = (props: AgentCreateProps) => {
  const { onCancel, onSuccess } = props;
  const dispatch = useDispatch();
  const errorMessage = useSelector(getCreateAgentErrorMessage);
  const defaultErrorMessage = useSelector(getCreateAgentDefaultErrorMessage);

  useEffect(() => {
    if (defaultErrorMessage) {
      dispatch(showFailureNotification(defaultErrorMessage, dispatch(resetCreateAgentDefaultErrorMessage())));
    }
  }, [defaultErrorMessage, dispatch]);

  useEffect(() => {
    dispatch(resetCreateAgentErrorMessage());
    dispatch(resetCreateAgentDefaultErrorMessage());
  }, [dispatch]);

  const onEmailChanged = useCallback(() => {
    if (errorMessage) {
      dispatch(resetCreateAgentErrorMessage());
    }
  }, [dispatch, errorMessage]);

  const submit = useCallback(
    async (values: AgentCreateFormValues, { setSubmitting }: FormikHelpers<AgentCreateFormValues>) => {
      const agent = { ...values, email: values.email.trim(), phoneNumber: normalizePhoneNumber(values.phoneNumber) };
      // @ts-expect-error redux
      const result = await dispatch(createAgent({ agent }));
      setSubmitting(false);
      // @ts-expect-error redux
      if (!result.error) {
        onSuccess();
      }
    },
    [dispatch, onSuccess],
  );

  return (
    <Formik<AgentCreateFormValues>
      initialValues={initialData}
      validationSchema={AgentCreateValidationSchema}
      onSubmit={submit}
    >
      {({ isSubmitting }) => (
        <Form className={styles.agentCreateForm}>
          <div className={styles.row}>
            <FormikSelect label="Anrede" options={SALUTATION} labelKey="value" valueKey="key" name="salutation" />
          </div>

          <div className={styles.row}>
            <FormikInput label="Vorname*" type="text" name="firstName" />

            <FormikInput label="Nachname*" type="text" name="lastName" />
          </div>

          <div className={styles.row}>
            {/* Note: using onChange/onFocus might clash with formik-validation */}
            <FormikInput label="Email*" type="text" name="email" onKeyUp={onEmailChanged} />

            <FormikInput label="Telefonnumer" name="phoneNumber" />
          </div>

          {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}

          <div className={styles.buttonRow}>
            <Button className={styles.modalButtonSecondary} type="button" disabled={isSubmitting} onClick={onCancel}>
              Abbrechen
            </Button>
            <Button className={styles.modalButton} type="submit" disabled={isSubmitting}>
              Speichern
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default AgentCreate;
